import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { react } from "@tldraw/state";
import { useQuickReactor, useStateTracking } from "@tldraw/state-react";
import { warnOnce } from "@tldraw/utils";
import { memo, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useEditorComponents } from "../hooks/EditorComponentsContext.mjs";
import { useEditor } from "../hooks/useEditor.mjs";
import { useShapeCulling } from "../hooks/useShapeCulling.mjs";
import { Mat } from "../primitives/Mat.mjs";
import { areShapesContentEqual } from "../utils/areShapesContentEqual.mjs";
import { moveElementInto, setStyleProperty } from "../utils/dom.mjs";
import { OptionalErrorBoundary } from "./ErrorBoundary.mjs";
const Shape = memo(function Shape2({
  id,
  shape,
  util,
  index,
  backgroundIndex,
  opacity
}) {
  const editor = useEditor();
  const { ShapeErrorFallback, ShapeWrapper } = useEditorComponents();
  const containerRef = useRef(null);
  const bgContainerRef = useRef(null);
  useEffect(() => {
    return react("load fonts", () => {
      const fonts = editor.fonts.getShapeFontFaces(id);
      editor.fonts.requestFonts(fonts);
    });
  }, [editor, id]);
  const memoizedStuffRef = useRef({
    transform: "",
    clipPath: "none",
    width: 0,
    height: 0,
    x: 0,
    y: 0
  });
  useQuickReactor(
    "set shape stuff",
    () => {
      const shape2 = editor.getShape(id);
      if (!shape2) return;
      const prev = memoizedStuffRef.current;
      const clipPath = editor.getShapeClipPath(id) ?? "none";
      if (clipPath !== prev.clipPath) {
        setStyleProperty(containerRef.current, "clip-path", clipPath);
        setStyleProperty(bgContainerRef.current, "clip-path", clipPath);
        prev.clipPath = clipPath;
      }
      const pageTransform = editor.getShapePageTransform(id);
      const transform = Mat.toCssString(pageTransform);
      const bounds = editor.getShapeGeometry(shape2).bounds;
      if (transform !== prev.transform) {
        setStyleProperty(containerRef.current, "transform", transform);
        setStyleProperty(bgContainerRef.current, "transform", transform);
        prev.transform = transform;
      }
      const width = Math.max(bounds.width, 1);
      const height = Math.max(bounds.height, 1);
      if (width !== prev.width || height !== prev.height) {
        setStyleProperty(containerRef.current, "width", width + "px");
        setStyleProperty(containerRef.current, "height", height + "px");
        setStyleProperty(bgContainerRef.current, "width", width + "px");
        setStyleProperty(bgContainerRef.current, "height", height + "px");
        prev.width = width;
        prev.height = height;
      }
    },
    [editor]
  );
  useLayoutEffect(() => {
    const container = containerRef.current;
    const bgContainer = bgContainerRef.current;
    setStyleProperty(container, "opacity", opacity);
    setStyleProperty(bgContainer, "opacity", opacity);
    setStyleProperty(container, "z-index", index);
    setStyleProperty(bgContainer, "z-index", backgroundIndex);
  }, [opacity, index, backgroundIndex]);
  const { register, unregister } = useShapeCulling();
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const isCulled = editor.getCulledShapes().has(id);
    register(id, container, bgContainerRef.current, isCulled);
    return () => {
      unregister(id);
    };
  }, [editor, id, register, unregister]);
  const annotateError = useCallback(
    (error) => editor.annotateError(error, { origin: "shape", willCrashApp: false }),
    [editor]
  );
  if (!shape || !ShapeWrapper) return null;
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    util.backgroundComponent && /* @__PURE__ */ jsx(ShapeWrapper, { ref: bgContainerRef, shape, isBackground: true, children: /* @__PURE__ */ jsx(OptionalErrorBoundary, { fallback: ShapeErrorFallback, onError: annotateError, children: /* @__PURE__ */ jsx(InnerShapeBackground, { shape, util }) }) }),
    /* @__PURE__ */ jsxs(ShapeWrapper, { ref: containerRef, shape, isBackground: false, children: [
      /* @__PURE__ */ jsx(OptionalErrorBoundary, { fallback: ShapeErrorFallback, onError: annotateError, children: /* @__PURE__ */ jsx(InnerShape, { shape, util }) }),
      util.getAppOwnedElement && /* @__PURE__ */ jsx(ContentElementSlot, { id, util })
    ] })
  ] });
});
const ContentElementSlot = memo(function ContentElementSlot2({
  id,
  util
}) {
  const editor = useEditor();
  const slotRef = useRef(null);
  useLayoutEffect(() => {
    const slot = slotRef.current;
    if (!slot) return;
    const shape = editor.getShape(id);
    if (!shape) return;
    const element = util.getAppOwnedElement?.(shape);
    if (!element) return;
    const wasConnectedBeforeAdoption = element.isConnected;
    if (element.parentNode !== slot) {
      moveElementInto(slot, element);
    }
    if (process.env.NODE_ENV !== "production" && wasConnectedBeforeAdoption) {
      warnIfContentElementReloads(editor, slot, element);
    }
    return () => {
      const latestShape = editor.getShape(id) ?? shape;
      util.onReleaseAppOwnedElement?.(latestShape, element);
      if (process.env.NODE_ENV !== "production" && element.parentNode === slot) {
        warnOnce(
          `The content element for shape "${id}" was not reclaimed by onReleaseAppOwnedElement and will be destroyed along with its slot, losing any state it holds.`
        );
      }
    };
  }, [editor, id, util]);
  return /* @__PURE__ */ jsx("div", { ref: slotRef, className: "tl-content-slot", draggable: false });
});
function warnIfContentElementReloads(editor, slot, element) {
  const onLoad = (event) => {
    if (event.target !== element && !element.contains(event.target)) return;
    warnOnce(
      "A load event fired on an adopted content element. The element reloaded when it was moved into the shape, losing its state. State-preserving moves need Node.moveBefore (Chromium 133+, Firefox 144+) and a continuously connected element."
    );
  };
  slot.addEventListener("load", onLoad, { capture: true });
  editor.timers.setTimeout(() => slot.removeEventListener("load", onLoad, { capture: true }), 1e3);
}
const InnerShape = memo(
  function InnerShape2({ shape, util }) {
    return useStateTracking(
      "InnerShape:" + shape.type,
      () => (
        // always fetch the latest shape from the store even if the props/meta have not changed, to avoid
        // calling the render method with stale data.
        (util.component(util.editor.store.unsafeGetWithoutCapture(shape.id)))
      ),
      [util, shape.id]
    );
  },
  (prev, next) => areShapesContentEqual(prev.shape, next.shape) && prev.util === next.util
);
const InnerShapeBackground = memo(
  function InnerShapeBackground2({
    shape,
    util
  }) {
    return useStateTracking(
      "InnerShape:" + shape.type,
      () => (
        // always fetch the latest shape from the store even if the props/meta have not changed, to avoid
        // calling the render method with stale data.
        (util.backgroundComponent?.(util.editor.store.unsafeGetWithoutCapture(shape.id)))
      ),
      [util, shape.id]
    );
  },
  (prev, next) => prev.shape.props === next.shape.props && prev.shape.meta === next.shape.meta && prev.util === next.util
);
export {
  InnerShape,
  InnerShapeBackground,
  Shape
};
//# sourceMappingURL=Shape.mjs.map
